import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  MessageSquarePlus,
  Scale,
  ShieldCheck,
  UserRoundSearch,
} from 'lucide-react';
import {
  DUPLICATE_RESOLUTIONS,
  DUPLICATE_STATUSES,
  DUPLICATE_STATUS_META,
} from '../config/duplicateStatus';
import { duplicateService } from '../services';
import { useAuth } from '../context/AuthContext';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import SearchInput from '../components/forms/SearchInput';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';
import Table from '../components/tables/Table';

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  ...Object.entries(DUPLICATE_STATUS_META).map(([value, meta]) => ({
    value,
    label: meta.label,
  })),
];

const resolutionOptions = [
  { value: DUPLICATE_RESOLUTIONS.KEEP_CURRENT, label: 'Conservar el puntero actual y anular el otro registro' },
  { value: DUPLICATE_RESOLUTIONS.REASSIGN, label: 'Elegir el puntero correcto y anular el registro incorrecto' },
  { value: DUPLICATE_RESOLUTIONS.SHARE, label: 'Marcar asociación compartida' },
  { value: DUPLICATE_RESOLUTIONS.REJECT, label: 'Rechazar solicitud' },
  { value: DUPLICATE_RESOLUTIONS.INVALID, label: 'Marcar como no válido' },
];

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('es-PY', { dateStyle: 'medium', timeStyle: 'short' })
      .format(new Date(value))
    : '—';

function StatusBadge({ status }) {
  const meta = DUPLICATE_STATUS_META[status] || {
    label: status,
    variant: 'neutral',
  };
  return <Badge variant={meta.variant} dot>{meta.label}</Badge>;
}

function DuplicatesPage() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [selected, setSelected] = useState(null);
  const [observation, setObservation] = useState('');
  const [resolution, setResolution] = useState(DUPLICATE_RESOLUTIONS.KEEP_CURRENT);
  const [definitiveLeaderId, setDefinitiveLeaderId] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await duplicateService.getForActor(user, { query, status });
      setRecords(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [query, status, user]);

  useEffect(() => {
    const timer = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, query]);

  const totals = useMemo(() => ({
    open: records.filter((item) =>
      [DUPLICATE_STATUSES.PENDING, DUPLICATE_STATUSES.IN_REVIEW].includes(item.status)
    ).length,
    shared: records.filter((item) => item.status === DUPLICATE_STATUSES.SHARED).length,
    closed: records.filter((item) =>
      ![DUPLICATE_STATUSES.PENDING, DUPLICATE_STATUSES.IN_REVIEW].includes(item.status)
    ).length,
  }), [records]);

  const openCase = (record) => {
    setSelected(record);
    setObservation('');
    setResolution(DUPLICATE_RESOLUTIONS.KEEP_CURRENT);
    setDefinitiveLeaderId(record.requestedLeaderId);
    setResolutionNotes('');
    setError('');
  };

  const refreshSelected = async () => {
    const response = await duplicateService.getForActor(user);
    const updated = response.data.find((item) => item.id === selected.id);
    setSelected(updated || null);
    await load();
  };

  const saveObservation = async () => {
    setSaving(true);
    setError('');
    try {
      await duplicateService.addObservation(selected.id, observation, user);
      setObservation('');
      setFeedback('Observación registrada. El caso quedó en consulta.');
      await refreshSelected();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const resolveCase = async () => {
    setSaving(true);
    setError('');
    try {
      await duplicateService.resolve(selected.id, {
        resolution,
        definitiveLeaderId,
        notes: resolutionNotes,
      }, user);
      setSelected(null);
      setFeedback('El conflicto fue resuelto y la operación quedó registrada en auditoría.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'document',
      header: 'Votante',
      render: (_, item) => (
        <div>
          <p className="font-semibold text-content-primary">{item.voterName}</p>
          <p className="mt-0.5 text-xs text-content-muted">Cédula {item.document}</p>
        </div>
      ),
    },
    {
      key: 'currentLeaderName',
      header: 'Responsable actual',
      render: (_, item) => (
        <div>
          <p className="font-medium text-content-primary">{item.currentLeaderName}</p>
          <p className="text-xs text-content-muted">{item.currentCoordinatorName}</p>
        </div>
      ),
    },
    {
      key: 'requestedLeaderName',
      header: 'Nuevo solicitante',
      render: (_, item) => (
        <div>
          <p className="font-medium text-content-primary">{item.requestedLeaderName}</p>
          <p className="text-xs text-content-muted">{item.requestedCoordinatorName}</p>
        </div>
      ),
    },
    { key: 'status', header: 'Estado', render: (value) => <StatusBadge status={value} /> },
    { key: 'createdAt', header: 'Creado', render: (value) => formatDate(value) },
    {
      key: 'actions',
      header: 'Acciones',
      render: (_, item) => (
        <Button variant="outline" size="sm" onClick={() => openCase(item)}>
          Consultar
        </Button>
      ),
    },
  ];

  const isOpen = selected && [
    DUPLICATE_STATUSES.PENDING,
    DUPLICATE_STATUSES.IN_REVIEW,
  ].includes(selected.status);
  const canAddNote = selected?.allowedActions?.includes('add_note');
  const canResolve = selected?.allowedActions?.includes('keep_current');

  return (
    <div>
      <div className="mb-7">
        <h2 className="text-2xl font-semibold tracking-tight text-content-primary">
          Gestión de duplicados
        </h2>
        <p className="mt-1 text-sm text-content-muted">
          Conflictos de asociación por cédulas ya registradas. Cada persona se cuenta una sola vez.
        </p>
      </div>

      {feedback && (
        <Alert variant="success" title="Operación completada" className="mb-5">
          {feedback}
        </Alert>
      )}
      {error && !selected && <Alert variant="danger" title="No se pudo cargar" className="mb-5">{error}</Alert>}

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-sm text-content-muted">Casos abiertos</p>
          <p className="mt-1 text-2xl font-semibold text-content-primary">{totals.open}</p>
        </Card>
        <Card>
          <p className="text-sm text-content-muted">Asociaciones compartidas</p>
          <p className="mt-1 text-2xl font-semibold text-content-primary">{totals.shared}</p>
        </Card>
        <Card>
          <p className="text-sm text-content-muted">Casos cerrados</p>
          <p className="mt-1 text-2xl font-semibold text-content-primary">{totals.closed}</p>
        </Card>
      </div>

      <Card className="mb-5">
        <div className="grid gap-4 sm:grid-cols-[1fr_220px]">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery('')}
            placeholder="Buscar por cédula, votante o puntero"
            aria-label="Buscar conflictos"
          />
          <Select
            id="duplicate-status-filter"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            options={statusOptions}
            aria-label="Filtrar por estado"
          />
        </div>
      </Card>

      {loading ? (
        <div className="grid min-h-64 place-items-center"><LoadingSpinner label="Cargando conflictos" /></div>
      ) : records.length ? (
        <Table columns={columns} data={records} />
      ) : (
        <EmptyState
          icon={UserRoundSearch}
          title="No hay conflictos"
          description="No se encontraron asociaciones con los filtros seleccionados."
        />
      )}

      <Modal
        open={Boolean(selected)}
        onClose={() => !saving && setSelected(null)}
        size="lg"
        title="Detalle del conflicto de asociación"
        description={selected ? `${selected.voterName} · Cédula ${selected.document}` : ''}
      >
        {selected && (
          <div className="space-y-5">
            {error && <Alert variant="danger" title="No se pudo completar la acción">{error}</Alert>}
            <div className="flex items-center justify-between">
              <StatusBadge status={selected.status} />
              <span className="text-xs text-content-muted">{formatDate(selected.createdAt)}</span>
            </div>

            <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr]">
              <div className="rounded-control border bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-content-muted">Registro actual</p>
                <p className="mt-2 font-semibold text-content-primary">{selected.currentLeaderName}</p>
                <p className="mt-1 text-sm text-content-muted">Coordinador: {selected.currentCoordinatorName}</p>
                <p className="mt-2 text-xs text-content-muted">Registro de votante #{selected.currentVoterId}</p>
              </div>
              <ArrowRight className="m-auto size-5 text-content-muted" aria-hidden="true" />
              <div className="rounded-control border border-primary-100 bg-primary-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Registro duplicado</p>
                <p className="mt-2 font-semibold text-content-primary">{selected.requestedLeaderName}</p>
                <p className="mt-1 text-sm text-content-muted">Coordinador: {selected.requestedCoordinatorName}</p>
                <p className="mt-2 text-xs text-content-muted">Solicitado por {selected.requestedByName}</p>
                {selected.duplicateVoterId && <p className="mt-1 text-xs text-content-muted">Registro de votante #{selected.duplicateVoterId}</p>}
              </div>
            </div>

            <section>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-content-primary">
                <MessageSquarePlus className="size-4" /> Observaciones
              </h3>
              <div className="max-h-32 space-y-2 overflow-y-auto">
                {(selected.observations || []).map((item) => (
                  <div key={item.id} className="rounded-control bg-slate-50 p-3 text-sm">
                    <p className="text-content-secondary">{item.text}</p>
                    <p className="mt-1 text-xs text-content-muted">{item.userName} · {formatDate(item.createdAt)}</p>
                  </div>
                ))}
                {!selected.observations?.length && <p className="text-sm text-content-muted">Sin observaciones registradas.</p>}
              </div>
              {isOpen && canAddNote && (
                <div className="mt-3 flex items-end gap-3">
                  <Textarea
                    id="duplicate-observation"
                    label="Nueva observación"
                    className="flex-1"
                    value={observation}
                    onChange={(event) => setObservation(event.target.value)}
                  />
                  <Button variant="outline" onClick={saveObservation} loading={saving} disabled={!observation.trim()}>
                    Agregar
                  </Button>
                </div>
              )}
            </section>

            {isOpen && canResolve ? (
              <section className="rounded-control border border-primary-100 bg-primary-50/50 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-content-primary">
                  <Scale className="size-4" /> Resolución
                </h3>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Select
                    id="duplicate-resolution"
                    label="Decisión"
                    value={resolution}
                    onChange={(event) => setResolution(event.target.value)}
                    options={resolutionOptions}
                  />
                  {resolution === DUPLICATE_RESOLUTIONS.REASSIGN && (
                    <Select
                      id="duplicate-definitive-leader"
                      label="Responsable definitivo"
                      value={definitiveLeaderId}
                      onChange={(event) => setDefinitiveLeaderId(event.target.value)}
                      options={[
                        { value: selected.currentLeaderId, label: `${selected.currentLeaderName} · actual` },
                        { value: selected.requestedLeaderId, label: `${selected.requestedLeaderName} · solicitante` },
                      ]}
                    />
                  )}
                  <Textarea
                    id="duplicate-resolution-notes"
                    label="Fundamento u observación final"
                    className="sm:col-span-2"
                    value={resolutionNotes}
                    onChange={(event) => setResolutionNotes(event.target.value)}
                  />
                </div>
                <Alert variant="warning" title="La corrección es operativa" className="mt-4">
                  El registro correspondiente al puntero elegido permanecerá activo. El otro será anulado y dejará de contabilizarse, pero conservará su historial para auditoría.
                </Alert>
                <div className="mt-4 flex justify-end">
                  <Button onClick={resolveCase} loading={saving}>
                    <ShieldCheck className="size-4" /> Resolver caso
                  </Button>
                </div>
              </section>
            ) : !isOpen ? (
              <Alert variant="info" title="Caso cerrado">
                Resolución: {resolutionOptions.find((item) => item.value === selected.resolution)?.label || 'Caso finalizado'}.
                {selected.resolutionNotes && ` ${selected.resolutionNotes}`}
              </Alert>
            ) : (
              <Alert variant="info" title="Caso en revisión">
                Puedes consultar este conflicto, pero la resolución final corresponde a un administrador autorizado.
              </Alert>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default DuplicatesPage;
